import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Play, Square, RotateCw, Trash2, Terminal } from 'lucide-react'
import Card, { CardHeader } from '../components/Card'
import Button from '../components/Button'
import StatusBadge from '../components/StatusBadge'
import { getContainer, getContainerLogs, startContainer, stopContainer, restartContainer, destroyContainer } from '../lib/api'

export default function ContainerDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: container, isLoading } = useQuery({
    queryKey: ['container', id],
    queryFn: () => getContainer(id!),
    enabled: !!id,
    refetchInterval: 5000,
  })

  const { data: logs = [] } = useQuery({
    queryKey: ['containerLogs', id],
    queryFn: () => getContainerLogs(id!, 50),
    enabled: !!id,
    refetchInterval: 3000,
  })

  const startMutation = useMutation({
    mutationFn: () => startContainer(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['container', id] }),
  })

  const stopMutation = useMutation({
    mutationFn: () => stopContainer(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['container', id] }),
  })

  const restartMutation = useMutation({
    mutationFn: () => restartContainer(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['container', id] }),
  })

  const destroyMutation = useMutation({
    mutationFn: () => destroyContainer(id!),
    onSuccess: () => navigate('/containers'),
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-voltainer-600" />
      </div>
    )
  }

  if (!container) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">Container not found</h3>
        <p className="mt-2 text-gray-500">The container may have been removed</p>
        <Button onClick={() => navigate('/containers')} className="mt-4">
          Back to Containers
        </Button>
      </div>
    )
  }

  const handleDestroy = () => {
    if (confirm(`Are you sure you want to destroy ${id}?`)) {
      destroyMutation.mutate()
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/containers')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{container.id}</h1>
            <div className="flex items-center space-x-2 mt-1">
              <StatusBadge status={container.state} />
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {container.state !== 'running' ? (
            <Button
              onClick={() => startMutation.mutate()}
              loading={startMutation.isPending}
              icon={<Play className="h-4 w-4" />}
            >
              Start
            </Button>
          ) : (
            <>
              <Button
                variant="secondary"
                onClick={() => stopMutation.mutate()}
                loading={stopMutation.isPending}
                icon={<Square className="h-4 w-4" />}
              >
                Stop
              </Button>
              <Button
                variant="secondary"
                onClick={() => restartMutation.mutate()}
                loading={restartMutation.isPending}
                icon={<RotateCw className="h-4 w-4" />}
              >
                Restart
              </Button>
            </>
          )}
          <Button
            variant="danger"
            onClick={handleDestroy}
            loading={destroyMutation.isPending}
            icon={<Trash2 className="h-4 w-4" />}
          >
            Destroy
          </Button>
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Info */}
        <Card>
          <CardHeader title="Details" />
          <dl className="space-y-3">
            <div>
              <dt className="text-sm text-gray-500">ID</dt>
              <dd className="font-mono text-sm">{container.id}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">State</dt>
              <dd><StatusBadge status={container.state} /></dd>
            </div>
            {container.ip_address && (
              <div>
                <dt className="text-sm text-gray-500">IP Address</dt>
                <dd className="font-mono text-sm">{container.ip_address}</dd>
              </div>
            )}
            {container.network_mode && (
              <div>
                <dt className="text-sm text-gray-500">Network Mode</dt>
                <dd className="text-sm">{container.network_mode}</dd>
              </div>
            )}
          </dl>
        </Card>

        {/* Logs */}
        <Card className="lg:col-span-2" padding="none">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 flex items-center">
                <Terminal className="h-4 w-4 mr-2" />
                Logs
              </h3>
            </div>
          </div>
          <div className="p-4 bg-gray-900 rounded-b-xl max-h-96 overflow-y-auto font-mono text-sm">
            {logs.length === 0 ? (
              <p className="text-gray-500">No logs available</p>
            ) : (
              logs.map((line, i) => (
                <div key={i} className="text-gray-300 whitespace-pre-wrap">
                  {line}
                </div>
              ))
            )}
          </div>
        </Card>
      </div>
    </div>
  )
}
